import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { destForCall, mapsFromTo } from "@/lib/routing";

const STATUS_COLOR = {
  dispatched: "#B07A00",
  accepted: "#0096FF",
  on_scene: "#0096FF",
  transporting: "#2C6BC2",
  delivered: "#1E7E34",
  completed: "#1E7E34",
  cancelled: "#B14037",
};

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function pinIcon(color) {
  return L.divIcon({
    className: "curtron-pin",
    html: `<div style="position:relative;width:26px;height:26px;">
      <span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.25;animation:pulse 2s infinite;"></span>
      <span style="position:absolute;left:5px;top:5px;width:16px;height:16px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);"></span>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -12],
  });
}

export function FleetMap({ transports, etas = {}, availableDrivers = [] }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([32.0, -81.3], 8);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const pts = [];
    (transports || []).forEach((c) => {
      try {
        const last = c.last;
        const color = STATUS_COLOR[c.status] || "#1F2D7A";
        if (Array.isArray(c.trail) && c.trail.length > 1) {
          const line = c.trail.filter((p) => p && isNum(p.lat) && isNum(p.lng)).map((p) => [p.lat, p.lng]);
          if (line.length > 1) L.polyline(line, { color, weight: 3, opacity: 0.6 }).addTo(layer);
        }
        if (last && isNum(last.lat) && isNum(last.lng)) {
          pts.push([last.lat, last.lng]);
          const driver = c.driver_name && c.driver_name !== "—" ? c.driver_name : "Unassigned";
          const eta = etas[c.id];
          const { address, label } = destForCall(c);

          // Dashed route line from driver's live position to the current destination.
          if (eta && eta.dest && isNum(eta.dest.lat) && isNum(eta.dest.lng)) {
            pts.push([eta.dest.lat, eta.dest.lng]);
            L.polyline([[last.lat, last.lng], [eta.dest.lat, eta.dest.lng]], { color, weight: 2, opacity: 0.7, dashArray: "6 6" }).addTo(layer);
            L.circleMarker([eta.dest.lat, eta.dest.lng], { radius: 5, color: "#fff", weight: 2, fillColor: color, fillOpacity: 1 })
              .bindPopup(`<div style="font-family:'IBM Plex Sans',sans-serif;font-size:12px;color:#0B1437;text-transform:capitalize;">${esc(label)}</div>`)
              .addTo(layer);
          }

          const etaLine = eta && eta.durationMin != null
            ? `<div style="margin-top:6px;font-size:12px;color:#1E7E34;font-weight:600;">ETA ~${esc(eta.durationMin)} min to ${esc(label)}${eta.distanceMi != null ? ` · ${esc(eta.distanceMi)} mi` : ""}</div>`
            : "";
          const navLink = `<a href="${esc(mapsFromTo(last, address))}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:#0096FF;text-decoration:none;">➤ Navigate from driver</a>`;

          L.marker([last.lat, last.lng], { icon: pinIcon(color) })
            .bindPopup(
              `<div style="font-family:'IBM Plex Sans',sans-serif;min-width:180px;">
                <div style="font-weight:600;color:#0B1437;">${esc(c.deceased_name || "—")}</div>
                <div style="font-size:12px;color:#4A5270;">${esc(c.agency_for || "—")}</div>
                <div style="font-size:12px;color:#4A5270;margin-top:4px;">Driver: ${esc(driver)}</div>
                <div style="display:inline-block;margin-top:6px;font-size:11px;font-weight:600;color:${color};text-transform:capitalize;">${esc((c.status || "").replace(/_/g, " "))}</div>
                ${etaLine}
                ${navLink}
              </div>`
            )
            .addTo(layer);
        }
      } catch (err) {
        console.debug("Skipped malformed fleet record:", err);
      }
    });

    // Available drivers (online, not on an active call) — grey markers.
    (availableDrivers || []).forEach((d) => {
      try {
        if (!isNum(d.lat) || !isNum(d.lng)) return;
        pts.push([d.lat, d.lng]);
        L.circleMarker([d.lat, d.lng], { radius: 7, color: "#fff", weight: 2, fillColor: "#8A90A3", fillOpacity: 1 })
          .bindPopup(`<div style="font-family:'IBM Plex Sans',sans-serif;min-width:140px;">
            <div style="font-weight:600;color:#0B1437;">${esc(d.name || "Driver")}</div>
            <div style="font-size:12px;color:#4A5270;margin-top:2px;">Available · not on a call</div>
          </div>`)
          .addTo(layer);
      } catch (err) { console.debug("Skipped malformed driver record:", err); }
    });

    if (pts.length === 1) {
      map.setView(pts[0], 12);
    } else if (pts.length > 1) {
      map.fitBounds(L.latLngBounds(pts).pad(0.25));
    }
  }, [transports, etas, availableDrivers]);

  const hasPoints = (transports || []).some((c) => c.last && isNum(c.last.lat) && isNum(c.last.lng)) || (availableDrivers || []).some((d) => isNum(d.lat) && isNum(d.lng));

  return (
    <div className="relative">
      <div ref={elRef} data-testid="fleet-map" className="w-full h-[420px] rounded-md overflow-hidden border border-curtron-line z-0" />
      {!hasPoints && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-md pointer-events-none">
          <div className="text-sm text-curtron-sub bg-white border border-curtron-line rounded-md px-4 py-2 shadow-sm">No live driver locations yet</div>
        </div>
      )}
    </div>
  );
}
